import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Building2, Mail, User, Globe, Users, MessageSquare, 
  Send, CheckCircle, ArrowLeft, Briefcase, DollarSign,
  Handshake, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Logo } from '../components/Logo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PartnerContactPage() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    business_name: '',
    website: '',
    business_type: '',
    num_clients: '',
    services_interested: [],
    message: ''
  });

  const businessTypes = [
    'Marketing Agency',
    'Digital Agency',
    'SaaS Company',
    'Consulting Firm',
    'Reseller/Affiliate',
    'Other'
  ];

  const clientRanges = [
    '1-10 clients',
    '11-50 clients',
    '51-100 clients',
    '100+ clients'
  ];

  const servicesOptions = [
    'White-label CRM/BDC-X',
    'Cinematic Ad System',
    'Managed Paid Ads',
    'AI Tools & Automation',
    'Full Workspace Access for Clients',
    'Revenue Sharing Program'
  ];

  const handleServiceToggle = (service) => {
    setFormData(prev => ({
      ...prev,
      services_interested: prev.services_interested.includes(service)
        ? prev.services_interested.filter(s => s !== service)
        : [...prev.services_interested, service]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/partner-inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success('Your inquiry has been submitted! We\'ll be in touch soon.');
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full text-center"
        >
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
          <h1 className="text-3xl font-heading font-bold text-white mb-4">
            Thank You!
          </h1>
          <p className="text-gray-400 mb-6">
            Your partner inquiry has been received. Our team will review your application and reach out within 1-2 business days to discuss revenue sharing opportunities and special channel partner pricing.
          </p>
          <Link to="/">
            <Button className="btn-primary">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/">
            <Logo size="md" showText={true} />
          </Link>
          <Link to="/pricing">
            <Button variant="outline" size="sm">View Pricing</Button>
          </Link>
        </div>
      </header>

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/30 mb-6">
              <Handshake className="w-4 h-4 text-primary" />
              <span className="text-sm text-primary font-medium">Partner Program</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-heading font-bold text-white mb-4">
              Agency & SaaS{' '}
              <span className="text-gradient">Partner Program</span>
            </h1>
            
            <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-6">
              Scale your business with AI Horse-Powered POD™. Get special channel partner pricing, 
              white-label options, and revenue sharing opportunities.
            </p>

            {/* Revenue Sharing Highlight */}
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-xl bg-green-500/10 border border-green-500/30">
              <DollarSign className="w-5 h-5 text-green-400" />
              <span className="text-green-400 font-medium">
                Revenue Sharing Account provided when active
              </span>
              <Sparkles className="w-5 h-5 text-green-400" />
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass rounded-2xl p-8 border border-white/10"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="full_name" className="text-gray-300 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Full Name *
                  </Label>
                  <Input
                    id="full_name"
                    required
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    placeholder="John Smith"
                    className="bg-white/5 border-white/10"
                    data-testid="partner-full-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-300 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@agency.com"
                    className="bg-white/5 border-white/10"
                    data-testid="partner-email"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="business_name" className="text-gray-300 flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Business Name *
                  </Label>
                  <Input
                    id="business_name"
                    required
                    value={formData.business_name}
                    onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                    placeholder="Acme Marketing Agency"
                    className="bg-white/5 border-white/10"
                    data-testid="partner-business-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website" className="text-gray-300 flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    Website
                  </Label>
                  <Input
                    id="website"
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://www.youragency.com"
                    className="bg-white/5 border-white/10"
                    data-testid="partner-website"
                  />
                </div>
              </div>

              {/* Business Type */}
              <div className="space-y-3">
                <Label className="text-gray-300 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  Business Type *
                </Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {businessTypes.map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setFormData({ ...formData, business_type: type })}
                      className={`px-4 py-2 rounded-lg border text-sm transition-all ${
                        formData.business_type === type
                          ? 'bg-primary/20 border-primary text-primary'
                          : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/20'
                      }`}
                      data-testid={`partner-type-${type.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {/* Number of Clients */}
              <div className="space-y-3">
                <Label className="text-gray-300 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  How many clients/sub-accounts do you manage?
                </Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {clientRanges.map((range) => (
                    <button
                      key={range}
                      type="button"
                      onClick={() => setFormData({ ...formData, num_clients: range })}
                      className={`px-4 py-2 rounded-lg border text-sm transition-all ${
                        formData.num_clients === range
                          ? 'bg-primary/20 border-primary text-primary'
                          : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/20'
                      }`}
                      data-testid={`partner-clients-${range.replace(/\s+/g, '-')}`}
                    >
                      {range}
                    </button>
                  ))}
                </div>
              </div>

              {/* Services Interested */}
              <div className="space-y-3">
                <Label className="text-gray-300 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Which services are you interested in? (Select all that apply)
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {servicesOptions.map((service) => (
                    <button
                      key={service}
                      type="button"
                      onClick={() => handleServiceToggle(service)}
                      className={`px-4 py-3 rounded-lg border text-sm text-left transition-all flex items-center gap-2 ${
                        formData.services_interested.includes(service)
                          ? 'bg-primary/20 border-primary text-primary'
                          : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/20'
                      }`}
                      data-testid={`partner-service-${service.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                        formData.services_interested.includes(service)
                          ? 'bg-primary border-primary'
                          : 'border-white/30'
                      }`}>
                        {formData.services_interested.includes(service) && (
                          <CheckCircle className="w-3 h-3 text-white" />
                        )}
                      </div>
                      {service}
                    </button>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="message" className="text-gray-300 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Tell us about your business and goals
                </Label>
                <textarea
                  id="message"
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Share details about your agency, your clients, and what you're looking to achieve with our partner program..."
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                  data-testid="partner-message"
                />
              </div>

              {/* Submit */}
              <div className="pt-4">
                <Button
                  type="submit"
                  disabled={loading || !formData.full_name || !formData.email || !formData.business_name || !formData.business_type}
                  className="w-full btn-primary text-lg py-6"
                  data-testid="partner-submit-btn"
                >
                  {loading ? (
                    <>Processing...</>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Submit Partner Inquiry
                    </>
                  )}
                </Button>
                <p className="text-center text-sm text-gray-500 mt-4">
                  We typically respond within 1-2 business days
                </p>
              </div>
            </form>
          </motion.div>

          {/* Benefits */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-12 grid sm:grid-cols-3 gap-6"
          >
            {[
              {
                icon: DollarSign,
                title: 'Revenue Sharing',
                description: 'Earn recurring revenue for every active client you bring to the platform'
              },
              {
                icon: Building2,
                title: 'Volume Discounts',
                description: 'Special pricing tiers based on the number of sub-accounts you manage'
              },
              {
                icon: Handshake,
                title: 'Dedicated Support',
                description: 'Priority support and a dedicated partner success manager'
              }
            ].map((benefit, i) => (
              <div key={i} className="glass rounded-xl p-6 border border-white/10 text-center">
                <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-sm text-gray-400">{benefit.description}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
